import React from 'react'

import { useTheme } from '@mui/material'

import Box from 'components_new/atoms/Box'
import TableCell from 'components_new/atoms/Table/TableCell'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import TrendTag from 'components_new/molecules/TrendTag'

import {
  // ellipseLabel,
  getCellAlignment,
  getCellScaleSx,
  getStickyCellSx
} from '../utils'
import { TableDataType, TableStatus } from '../index'

interface BodyCellProps {
  color?: string
  divider: boolean
  increaseIsPositive?: boolean
  label: string | number | null | string[] | number[]
  // numberOfCharacters?: number
  percentageDiff?: number | null
  scaleFactor: number
  showTrendTag?: boolean
  status?: TableStatus
  // set on a pinned column: how far from the edge it sits
  stickyLeft?: number
  type: TableDataType
  variant?: 'body' | 'footer' | 'head'
}

const BodyCell = (props: BodyCellProps) => {
  const {
    color,
    divider,
    increaseIsPositive,
    label,
    // numberOfCharacters,
    percentageDiff,
    scaleFactor,
    showTrendTag,
    status,
    stickyLeft,
    type,
    variant
  } = props

  const theme = useTheme()

  return (
    <TableCell
      align={getCellAlignment(type)}
      sx={{
        borderRight: divider ? '1px solid' : undefined,
        '&:last-child': { borderRight: 0 },
        borderRightColor: 'divider',
        whiteSpace: 'nowrap',
        width: '1%', // For auto sized, fit content cells. (Hacker).
        ...getCellScaleSx(scaleFactor),
        ...(stickyLeft !== undefined
          ? getStickyCellSx(theme, false, stickyLeft)
          : undefined)
      }}
      variant={variant}
    >
      <TableCellBody
        color={color}
        increaseIsPositive={increaseIsPositive}
        label={label}
        percentageDiff={percentageDiff}
        showTrendTag={showTrendTag}
        status={status}
        type={type}
      />
    </TableCell>
  )
}

interface TableCellBodyProps {
  color?: string
  increaseIsPositive?: boolean
  label: string | number | string[] | number[] | null
  percentageDiff?: number | null
  showTrendTag?: boolean
  status?: TableStatus
  type: TableDataType
}

const TableCellBody = (props: TableCellBodyProps) => {
  const {
    color,
    increaseIsPositive,
    label,
    percentageDiff,
    showTrendTag,
    status,
    type
  } = props

  if (type === TableDataType.ENUM) {
    return (
      // Use Chip in next release: <Chip label={label} size="small" />
      <Box sx={{ display: 'flex', gridGap: 4, justifyContent: 'center' }}>
        {(label as string[]).map((item, i) => (
          <Tag key={i} label={item} color={status ? status[i] : undefined} />
        ))}
      </Box>
    )
  }

  if (
    type === TableDataType.STRING ||
    type === TableDataType.DATE ||
    type === TableDataType.NUMBER_TAG
  ) {
    const getTextColor = (i: number) =>
      color || (status ? `${status[i]}.text` : undefined)

    return (
      <Box
        sx={{
          display: 'flex',
          gridGap: 4,
          justifyContent:
            type === TableDataType.NUMBER_TAG || type === TableDataType.DATE
              ? 'flex-end'
              : undefined
        }}
      >
        {(label as string[]).map((item, index, labels) => (
          <Text
            component="span"
            fontSize="inherit"
            key={index}
            variant="body2"
            sx={{ color: getTextColor(index) }}
          >
            {/* {ellipseLabel(label, numberOfCharacters)} */}
            {item}
            {index < labels.length - 1 ? ', ' : ''}
          </Text>
        ))}
      </Box>
    )
  }

  return (
    <>
      <Text component="span" fontSize="inherit" variant="body2" sx={{ color }}>
        {/* {ellipseLabel(label, numberOfCharacters)} */}
        {label}
      </Text>
      {showTrendTag && increaseIsPositive !== undefined && percentageDiff ? (
        <TrendTag
          increaseIsPositive={increaseIsPositive}
          sx={{ ml: 1 }}
          value={percentageDiff}
        />
      ) : null}
    </>
  )
}

export default BodyCell
